import { useState } from 'react';
import { ColumnDef, TableState, VisibilityState } from '@tanstack/react-table';
import { useDeepCompareEffect } from '@anytag/shared-hooks';
import { generateVisibilityColumnState, getVisibleColumns } from '../utils';

interface Props<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  hasColumnCustomization?: boolean;
}

export const useTableState = <TData, TValue>({ columns, hasColumnCustomization = false }: Props<TData, TValue>) => {
  // defaultColumnVisibility can contain both invisible and visible columns -> { <column_name>: true|false }
  const defaultColumnVisibility = generateVisibilityColumnState(columns, hasColumnCustomization);
  // onColumnVisibilityChange only return the visible columns, so we need to filter the visible columns -> { <column_name>: true }
  const [visibleColumns, setVisibleColumns] = useState<VisibilityState>(getVisibleColumns(defaultColumnVisibility));

  // Update column visibility when state.columnVisibility changes.
  // Useful when some columns have visibility state based on fields from the API
  useDeepCompareEffect(() => {
    setVisibleColumns(getVisibleColumns(defaultColumnVisibility));
  }, [defaultColumnVisibility]);

  const stickyColumns = columns.reduce<string[]>((acc, curr) => {
    if ('accessorKey' in curr && curr.accessorKey && curr.meta?.sticky) {
      acc.push(curr.accessorKey as string);
    }

    return acc;
  }, []);

  return {
    setVisibleColumns,
    state: {
      columnPinning: { left: stickyColumns.length ? stickyColumns : undefined }, // We only support sticky left column for now
      columnVisibility: {
        ...defaultColumnVisibility, // Default input columnVisibility state
        ...(hasColumnCustomization ? visibleColumns : {}) // Table will manage the visible columns itself
      }
    } satisfies Partial<TableState>
  };
};
